import React, { useEffect, useState } from 'react';
import { IconBank, IconOpenBanking } from '@americanexpress/dls-react';

import useAsyncCallback from 'src/hooks/useAsyncCallback';
import { AccountDrawerType, AccountModel } from 'src/types/account.model';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import { ManualAccountType } from 'src/types/manualAccount.model';
import ManualAccountService from '../../../../../services/manualAccount.service';

import ListAccountsViewTemplate from '../listAccountsViewTemplate/ListAccountsViewTemplate';
import Callout from '../../../atoms/callout/Callout';
import WhyShouldYouConnectTooltip from '../../../atoms/tooltips/whyYouShouldConnectTooltip/WhyShouldYouConnectTooltip';
import LiteralsService from 'src/services/literals/LiteralsService';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { DLSSize } from 'src/types/dls-size.enum';
import { AddAccountListTemplate } from 'src/types/view-models/listAccounts/listAccountsViewModel.view.model';
import PropertyAndAssetsListAccountsViewModel from 'src/types/view-models/listAccounts/propertyAndAssets.view.model';
import Selectors from 'src/utils/selectors';

const addAccountListTemplate: AddAccountListTemplate = [
    {
        title: LiteralsService.getLiteral('Onboarding.propertyAndAssets.addRealestateTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.propertyAndAssets.addRealestateSubtitle'),
        manualAccountType: ManualAccountType.RealEstate
    },
    {
        title: LiteralsService.getLiteral('Onboarding.propertyAndAssets.addVehicleTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.propertyAndAssets.addVehicleSubtitle'),
        manualAccountType: ManualAccountType.Vehicle
    },
    {
        title: LiteralsService.getLiteral('Onboarding.propertyAndAssets.addCustomPropertyAndAssetTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.propertyAndAssets.addCustomPropertyAndAssetSubtitle'),
        manualAccountType: ManualAccountType.CustomPropertyAndAsset
    }
];

const callout = (
    <Callout
        icon={<IconBank size={DLSSize.MEDIUM} />}
        heading={LiteralsService.getLiteral('Onboarding.cashAndBanking.calloutHeading')}
    >
        <Typography variant={TypographyVariants.BODY}>
            {LiteralsService.getLiteral('Onboarding.cashAndBanking.calloutBody')}
        </Typography>
        <WhyShouldYouConnectTooltip withText />
    </Callout>
);

const formCallout = (
    <Callout
        icon={<IconOpenBanking size={DLSSize.MEDIUM} />}
        heading={LiteralsService.getLiteral('Onboarding.propertyAndAssets.formCalloutHeading')}
        body={LiteralsService.getLiteral('Onboarding.propertyAndAssets.formCalloutBody')}
    />
);

export default function PropertyAndAssetsListAccountsView({
    user,
    back,
    next,
    handleReloadUser
}: PropertyAndAssetsListAccountsViewModel): JSX.Element {
    const [visibleAccounts, setVisibleAccounts] = useState<AccountModel[]>([]);
    const [hiddenAccounts, setHiddenAccounts] = useState<AccountModel[]>([]);
    const [nonDrawerTypeAccounts, setNonDrawerTypeAccounts] = useState<AccountModel[]>();
    const [investmentHoldings, setInvestmentHoldings] = useState<InvestmentHoldingModel[]>([]);
    const [loading, setLoading] = useState(true);

    const refreshAccounts = useAsyncCallback(async (): Promise<void> => {
        /*
        Currently, we don't have any connected account types and subtypes for Property and Assets accounts.
        Hence, they are added as empty arrays. In the future, if we add connected account types and subtypes for
        this component, we should do the filtering in the Account Service, as it's done currently in the Cash And
        Banking, Investments and Retirement, and Loans and Debts component.
        */
        const newVisibleAccounts: AccountModel[] = [];
        const newHiddenAccounts: AccountModel[] = [];
        const nonDrawerTypeAccounts: AccountModel[] = [];
        const { newInvestmentHoldings } = await ManualAccountService.getAccounts();
        setVisibleAccounts(newVisibleAccounts);
        setHiddenAccounts(newHiddenAccounts);
        setNonDrawerTypeAccounts(nonDrawerTypeAccounts);
        setInvestmentHoldings(
            newInvestmentHoldings.filter((e) => ManualAccountService.isPropertyAndAssets({ investmentHolding: e }))
        );
        setLoading(false);
    }, []);

    useEffect(() => {
        void refreshAccounts();
    }, [user.accounts.length]);

    if (loading) return <></>;

    return (
        <ListAccountsViewTemplate
            progressIndex={5}
            accountDrawerType={AccountDrawerType.PropertyAndAssets}
            title={LiteralsService.getLiteral('Onboarding.propertyAndAssets.title')}
            listAccountsHeading={LiteralsService.getLiteral('Onboarding.propertyAndAssets.listAccountsHeading')}
            ManualAccountType={ManualAccountService.propertyAndAssetsAccountTypes}
            visibleAccounts={visibleAccounts}
            hiddenAccounts={hiddenAccounts}
            nonDrawerTypeAccounts={nonDrawerTypeAccounts}
            investmentHoldings={investmentHoldings}
            debts={[]}
            addAccountListTemplate={addAccountListTemplate}
            callout={callout}
            formCallout={formCallout}
            refreshAccounts={refreshAccounts}
            back={back}
            next={next}
            handleReloadUser={handleReloadUser}
            data-cy={Selectors.PlanAhead.PROPERTY_AND_ASSETS}
        />
    );
}
